/**
 * promotionRuleHelpers.ts
 *
 * Pure functions extracted from PromotionRule.tsx so they can be unit-tested
 * without a React render environment.
 *
 * See PROMOTION_RULE_BUSINESS_LOGIC.md for the full spec these implement.
 */

import { TargetingType } from '../../config'
import { AdGroupType } from '../ad_groups/AdGroupsTable'
import PromotionRuleItemsI = Models.PromotionRuleItemsI

// ─── Types ────────────────────────────────────────────────────────────────────

/** The boolean columns that can be toggled en-masse via a header checkbox. */
export type TogglableKey =
    | 'use_search_terms'
    | 'promotion_broad'
    | 'promotion_phrase'
    | 'promotion_exact'
    | 'promotion_products'
    | 'negation_phrase'
    | 'negation_exact'
    | 'negation_products'

export interface HeaderState {
    useSearchTermsIndeterminate: boolean
    useSearchTermsChecked: boolean
    promotionBroadIndeterminate: boolean
    promotionBroadChecked: boolean
    promotionPhraseIndeterminate: boolean
    promotionPhraseChecked: boolean
    promotionExactIndeterminate: boolean
    promotionExactChecked: boolean
    promotionProductsIndeterminate: boolean
    promotionProductsChecked: boolean
    negationPhraseIndeterminate: boolean
    negationPhraseChecked: boolean
    negationExactIndeterminate: boolean
    negationExactChecked: boolean
    negationProductsIndeterminate: boolean
    negationProductsChecked: boolean
}

// ─── Conflict detection ───────────────────────────────────────────────────────

/**
 * Returns true if any item in the list violates the Amazon routing constraints:
 *
 *   Rule A — Auto campaign with any promotion flag set.
 *   Rule B — Manual-campaign item that uses BOTH keyword-type routing
 *             (broad/phrase/exact promote or phrase/exact negate) AND
 *             product-type routing (promotion_products or negation_products).
 *
 * Matches the exact logic in PromotionRule.tsx's conflict-detection useEffect.
 * When an auto-campaign item is encountered the loop stops immediately; only
 * the first auto-campaign item is evaluated.
 */
export function detectConflict(items: PromotionRuleItemsI[]): boolean {
    for (const item of items) {
        if (!item) continue

        if (item.campaign_targeting_type === TargetingType.Auto) {
            // Auto campaigns must not have any promotion flags set.
            // Negations are always allowed on auto campaigns.
            const hasPromotions = !!(
                item.promotion_broad ||
                item.promotion_exact ||
                item.promotion_phrase ||
                item.promotion_products
            )
            // Return immediately — we stop checking after the first auto item.
            return hasPromotions
        }

        // Manual campaign: mixing keyword-type and product-type routing conflicts.
        const isUsingKeywords = !!(
            item.promotion_broad ||
            item.promotion_exact ||
            item.promotion_phrase ||
            item.negation_phrase ||
            item.negation_exact
        )
        const isUsingTargets = !!(item.promotion_products || item.negation_products)
        if (isUsingKeywords && isUsingTargets) return true
    }
    return false
}

// ─── Header checkbox state ────────────────────────────────────────────────────

const isIndeterminate = (used: number, total: number): boolean => used !== 0 && used !== total

const isChecked = (used: number, total: number): boolean => total > 0 && used === total

/**
 * Computes the checked / indeterminate state for every column header checkbox.
 *
 * Denominators per column (see PROMOTION_RULE_BUSINESS_LOGIC.md §4):
 *   - use_search_terms : all items
 *   - promotion_broad/phrase/exact : KEYWORD items only
 *   - promotion_products : TARGET items only
 *     (Note: the old count accidentally includes AUTO; documented in §6c of the
 *      business-logic doc as a legacy bug preserved here for exact parity.)
 *   - negation_phrase/exact : KEYWORD + AUTO items
 *   - negation_products : TARGET + AUTO items
 */
export function computeHeaderState(items: PromotionRuleItemsI[]): HeaderState {
    let useSearchTermsCount = 0
    let promotionBroadCount = 0
    let promotionPhraseCount = 0
    let promotionExactCount = 0
    let promotionProductsCount = 0
    let negationPhraseCount = 0
    let negationExactCount = 0
    let negationProductsCount = 0

    for (const i of items) {
        if (!i) continue

        if (i.use_search_terms) useSearchTermsCount++

        if (i.promotion_broad && i.ad_group_type === AdGroupType.Keyword) promotionBroadCount++

        if (i.promotion_phrase && i.ad_group_type === AdGroupType.Keyword) promotionPhraseCount++

        if (i.promotion_exact && i.ad_group_type === AdGroupType.Keyword) promotionExactCount++

        // Legacy: count includes AUTO (see §6c). Denominator uses TARGET-only.
        if (
            i.promotion_products &&
            (i.ad_group_type === AdGroupType.Target || i.ad_group_type === AdGroupType.Auto)
        )
            promotionProductsCount++

        if (
            i.negation_phrase &&
            (i.ad_group_type === AdGroupType.Keyword || i.ad_group_type === AdGroupType.Auto)
        )
            negationPhraseCount++

        if (
            i.negation_exact &&
            (i.ad_group_type === AdGroupType.Keyword || i.ad_group_type === AdGroupType.Auto)
        )
            negationExactCount++

        if (
            i.negation_products &&
            (i.ad_group_type === AdGroupType.Target || i.ad_group_type === AdGroupType.Auto)
        )
            negationProductsCount++
    }

    const totalCount = items.length
    const totalAutoAdGroups = items.filter((i) => i?.ad_group_type === AdGroupType.Auto).length
    const totalKwAdGroupCount = items.filter((i) => i?.ad_group_type === AdGroupType.Keyword).length
    const totalTargetAdGroupCount = items.filter(
        (i) => i?.ad_group_type === AdGroupType.Target,
    ).length

    return {
        useSearchTermsIndeterminate: isIndeterminate(useSearchTermsCount, totalCount),
        useSearchTermsChecked: isChecked(useSearchTermsCount, totalCount),

        promotionBroadIndeterminate: isIndeterminate(promotionBroadCount, totalKwAdGroupCount),
        promotionBroadChecked: isChecked(promotionBroadCount, totalKwAdGroupCount),

        promotionPhraseIndeterminate: isIndeterminate(promotionPhraseCount, totalKwAdGroupCount),
        promotionPhraseChecked: isChecked(promotionPhraseCount, totalKwAdGroupCount),

        promotionExactIndeterminate: isIndeterminate(promotionExactCount, totalKwAdGroupCount),
        promotionExactChecked: isChecked(promotionExactCount, totalKwAdGroupCount),

        // Denominator: TARGET only (see §6c note above)
        promotionProductsIndeterminate: isIndeterminate(
            promotionProductsCount,
            totalTargetAdGroupCount,
        ),
        promotionProductsChecked: isChecked(promotionProductsCount, totalTargetAdGroupCount),

        negationPhraseIndeterminate: isIndeterminate(
            negationPhraseCount,
            totalKwAdGroupCount + totalAutoAdGroups,
        ),
        negationPhraseChecked: isChecked(
            negationPhraseCount,
            totalKwAdGroupCount + totalAutoAdGroups,
        ),

        negationExactIndeterminate: isIndeterminate(
            negationExactCount,
            totalKwAdGroupCount + totalAutoAdGroups,
        ),
        negationExactChecked: isChecked(
            negationExactCount,
            totalKwAdGroupCount + totalAutoAdGroups,
        ),

        negationProductsIndeterminate: isIndeterminate(
            negationProductsCount,
            totalTargetAdGroupCount + totalAutoAdGroups,
        ),
        negationProductsChecked: isChecked(
            negationProductsCount,
            totalTargetAdGroupCount + totalAutoAdGroups,
        ),
    }
}

// ─── Bulk toggle ──────────────────────────────────────────────────────────────

/**
 * Applies a "select all / deselect all" operation to a column of rule items,
 * respecting per-type eligibility.
 *
 * When `value = false` every item is updated unconditionally.
 * When `value = true` ineligible items are force-set to `false`:
 *
 *   - Keyword-type columns (broad/phrase/exact promote, phrase/exact negate):
 *     TARGET items are ineligible → forced false.
 *   - Product columns (promotion_products, negation_products):
 *     KEYWORD items are ineligible → forced false.
 *   - Any promotion column (broad/phrase/exact/products promote):
 *     AUTO items are ineligible → forced false.
 *   - Negation columns on AUTO: AUTO is eligible for phrase/exact/products negation
 *     and is therefore NOT forced false for those.
 *
 * Returns a new array (immutable — does not mutate the input).
 */
export function applyToggleAll(
    items: PromotionRuleItemsI[],
    key: TogglableKey,
    value: boolean,
): PromotionRuleItemsI[] {
    return items.map((p) => {
        if (value) {
            // Enabling keyword-type routing: TARGET ad groups are ineligible.
            if (
                (key === 'promotion_broad' ||
                    key === 'promotion_exact' ||
                    key === 'promotion_phrase' ||
                    key === 'negation_exact' ||
                    key === 'negation_phrase') &&
                p.ad_group_type === AdGroupType.Target
            ) {
                return { ...p, [key]: false }
            }

            // Enabling product routing: KEYWORD ad groups are ineligible.
            if (
                (key === 'promotion_products' || key === 'negation_products') &&
                p.ad_group_type === AdGroupType.Keyword
            ) {
                return { ...p, [key]: false }
            }

            // Enabling any promotion: AUTO ad groups are ineligible.
            // (Negation columns are intentionally omitted here — auto can negate.)
            if (
                (key === 'promotion_broad' ||
                    key === 'promotion_exact' ||
                    key === 'promotion_phrase' ||
                    key === 'promotion_products') &&
                p.ad_group_type === AdGroupType.Auto
            ) {
                return { ...p, [key]: false }
            }
        }

        return { ...p, [key]: value }
    })
}
